import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { supabase } from '@/lib/supabase'
import { useUserOrganization } from '@/hooks/useUserOrganization'

interface Group {
  id: string
  name: string
  color: string | null
  sort_order: number
  entity_type: 'employee' | 'department'
}

interface GroupMembership {
  group_id: string
  entity_id: number
}

export interface GroupedOption {
  groupName: string
  groupColor: string | null
  groupType: 'department' | 'employee' | 'ungrouped'
  items: { label: string; value: number }[]
}

export function useEmployeeGroups() {
  const { data: membership } = useUserOrganization()
  const orgId = membership?.organization_id

  const groupsQuery = useQuery({
    queryKey: ['groups', 'employee+department', orgId],
    queryFn: async () => {
      const { data, error } = await supabase
        .schema('core')
        .from('groups')
        .select('id, name, color, sort_order, entity_type')
        .eq('organization_id', orgId!)
        .in('entity_type', ['employee', 'department'])
        .order('sort_order')

      if (error) throw error
      return (data || []) as Group[]
    },
    enabled: !!orgId,
    staleTime: 5 * 60_000,
  })

  const membershipsQuery = useQuery({
    queryKey: ['group_memberships', 'employee+department', orgId],
    queryFn: async () => {
      const groupIds = groupsQuery.data?.map((g) => g.id) ?? []
      if (groupIds.length === 0) return []

      const { data, error } = await supabase
        .schema('core')
        .from('group_memberships')
        .select('group_id, entity_id')
        .in('group_id', groupIds)

      if (error) throw error
      return (data || []) as GroupMembership[]
    },
    enabled: !!orgId && (groupsQuery.data?.length ?? 0) > 0,
    staleTime: 5 * 60_000,
  })

  return {
    groups: groupsQuery.data ?? [],
    memberships: membershipsQuery.data ?? [],
    isLoading: groupsQuery.isLoading || membershipsQuery.isLoading,
  }
}

/**
 * Groups employees by their group membership.
 * Returns grouped options + "Øvrige" for ungrouped employees.
 */
export function useGroupedEmployees(
  employees: { employee_id: number; full_name: string }[]
) {
  const { groups, memberships } = useEmployeeGroups()

  return useMemo(() => {
    if (groups.length === 0) return null // no groups configured → flat list

    const departments = groups.filter((g) => g.entity_type === 'department')
    const employeeGroups = groups.filter((g) => g.entity_type === 'employee')

    // Build entity_id → group_ids map (employee can be in multiple groups)
    const entityToGroup = new Map<number, string>()
    for (const m of memberships) {
      entityToGroup.set(m.entity_id, m.group_id)
    }

    const grouped: GroupedOption[] = []
    const ungrouped: { label: string; value: number }[] = []

    // Build group buckets
    const groupBuckets = new Map<string, { label: string; value: number }[]>()
    for (const g of groups) {
      groupBuckets.set(g.id, [])
    }

    for (const emp of employees) {
      const gId = entityToGroup.get(emp.employee_id)
      if (gId && groupBuckets.has(gId)) {
        groupBuckets.get(gId)!.push({ label: emp.full_name, value: emp.employee_id })
      } else {
        ungrouped.push({ label: emp.full_name, value: emp.employee_id })
      }
    }

    // Departments first
    for (const g of departments) {
      const items = groupBuckets.get(g.id) ?? []
      if (items.length > 0) {
        grouped.push({ groupName: g.name, groupColor: g.color, groupType: 'department', items })
      }
    }

    // Then custom employee groups
    for (const g of employeeGroups) {
      const items = groupBuckets.get(g.id) ?? []
      if (items.length > 0) {
        grouped.push({ groupName: g.name, groupColor: g.color, groupType: 'employee', items })
      }
    }

    // Ungrouped last
    if (ungrouped.length > 0) {
      grouped.push({ groupName: 'Øvrige', groupColor: null, groupType: 'ungrouped', items: ungrouped })
    }

    return grouped
  }, [groups, memberships, employees])
}
